import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { api } from "@/lib/api";
import { GOOGLE_MAPS_URL } from "@/lib/site";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-1">
    {[...Array(5)].map((_, s) => (
      <Star key={s} className={`h-4 w-4 ${s < n ? "fill-fall-amber text-fall-amber" : "text-slate-300"}`} />
    ))}
  </div>
);

export const Testimonials = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setLive(r.data)).catch(() => setLive(null));
  }, []);

  const isLive = live && (live.source === "google" || live.source === "cache") && live.reviews?.length > 0;

  // Hidden until live Google reviews are connected — no placeholder reviews are shown.
  if (!isLive) return null;

  return (
    <section data-testid="testimonials-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">Neighbors talking</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            Loved across the East Valley
          </h2>
          <a
            data-testid="google-reviews-link"
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-5 py-2.5 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
          >
            Read our reviews on Google <ExternalLink className="h-4 w-4" />
          </a>
          {isLive && (
            <p data-testid="google-rating-summary" className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-bold text-brand-navy shadow-sm">
              <Star className="h-4 w-4 fill-fall-amber text-fall-amber" />
              {live.rating?.toFixed(1)} · {live.userRatingCount} Google ratings
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">Live</span>
            </p>
          )}
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {live.reviews.slice(0, 3).map((r, i) => (
            <Reveal key={r.author?.uri || i} delay={i * 0.12}>
              <figure className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow hover:shadow-xl hover:shadow-brand-blue/10">
                <Stars n={r.rating || 5} />
                <blockquote className="mt-4 flex-1 text-base leading-relaxed text-slate-600">
                  &ldquo;{r.text || "(No written comment)"}&rdquo;
                </blockquote>
                <figcaption className="mt-6 flex items-center justify-between text-sm font-bold text-brand-navy">
                  <span>
                    {r.author?.name || "Google reviewer"}
                    {r.relativeTime && <span className="block text-xs font-semibold text-slate-400">{r.relativeTime}</span>}
                  </span>
                  {(r.googleMapsUri || r.author?.uri) && (
                    <a
                      href={r.googleMapsUri || r.author?.uri}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs font-semibold text-brand-blue hover:underline"
                    >
                      Google <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
